/**
 * useGeographyContext — manages the active region + locality selection.
 *
 * - Reads/writes localStorage for persistence across navigations.
 * - Exposes the active Region and optional Locality records.
 * - Exposes setGeography(regionSlug, localitySlug?) for the geography switcher.
 * - Falls back gracefully when localStorage is unavailable.
 */

import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { Region, Locality } from '@shared/schema';

import { fetchJson } from '@/lib/queryClient';
import {
  getPersistedGeography,
  persistGeography,
  clearPersistedGeography,
  DEFAULT_REGION_SLUG,
} from '@/lib/geography';

export interface GeographyContextType {
  region: Region | null;
  locality: Locality | null;
  regionSlug: string;
  localitySlug: string | undefined;
  isLoading: boolean;
  setGeography: (regionSlug: string, localitySlug?: string) => void;
  clearGeography: () => void;
}

export const GeographyContext = createContext<GeographyContextType>({
  region: null,
  locality: null,
  regionSlug: DEFAULT_REGION_SLUG,
  localitySlug: undefined,
  isLoading: true,
  setGeography: () => {},
  clearGeography: () => {},
});

export function useGeographyContext() {
  return useContext(GeographyContext);
}

export function useGeographyContextLogic(): GeographyContextType {
  const persisted = getPersistedGeography();
  const [regionSlug, setRegionSlug] = useState<string>(persisted.regionSlug);
  const [localitySlug, setLocalitySlug] = useState<string | undefined>(persisted.localitySlug);

  const { data: region = null, isLoading: regionLoading } = useQuery<Region>({
    queryKey: ['region', regionSlug],
    queryFn: () => fetchJson<Region>(`/api/regions/${regionSlug}`),
    staleTime: 1000 * 60 * 10,
  });

  const { data: locality = null, isLoading: localityLoading } = useQuery<Locality>({
    queryKey: ['locality', regionSlug, localitySlug],
    queryFn: async () =>
      (
        await fetchJson<{ region: Region; locality: Locality }>(
          `/api/regions/${regionSlug}/${localitySlug}`
        )
      ).locality,
    enabled: !!localitySlug,
    staleTime: 1000 * 60 * 10,
  });

  const setGeography = useCallback((rSlug: string, lSlug?: string) => {
    persistGeography({ regionSlug: rSlug, localitySlug: lSlug });
    setRegionSlug(rSlug);
    setLocalitySlug(lSlug);
  }, []);

  const clearGeography = useCallback(() => {
    clearPersistedGeography();
    setRegionSlug(DEFAULT_REGION_SLUG);
    setLocalitySlug(undefined);
  }, []);

  // Sync from URL if navigating directly to /regions/:regionSlug[/:localitySlug]
  // Only treat the third segment as a locality if it's not a known section slug.
  const SECTION_SLUGS = new Set([
    'classes',
    'creators',
    'calendar',
    'community',
    'locations',
    'search',
    'updates',
  ]);
  useEffect(() => {
    const match = window.location.pathname.match(/^\/regions\/([^/]+)(?:\/([^/]+))?/);
    if (match) {
      const urlRegion = match[1];
      const rawSegment = match[2];
      const urlLocality = rawSegment && !SECTION_SLUGS.has(rawSegment) ? rawSegment : undefined;
      if (urlRegion !== regionSlug || urlLocality !== localitySlug) {
        setGeography(urlRegion, urlLocality);
      }
    }
  }, [regionSlug, localitySlug, setGeography]); // eslint-disable-line react-hooks/exhaustive-deps

  const isLoading = regionLoading || (!!localitySlug && localityLoading);

  return { region, locality, regionSlug, localitySlug, isLoading, setGeography, clearGeography };
}
